import { CdkMenuTrigger } from '@angular/cdk/menu';
import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SiAccordionModule } from '@siemens/element-ng/accordion';
import { ActionDialog, SiActionDialogService } from '@siemens/element-ng/action-modal';
import { SiCircleStatusModule } from '@siemens/element-ng/circle-status';
import { SiEmptyStateModule } from '@siemens/element-ng/empty-state';
import { SiFormModule } from '@siemens/element-ng/form';
import { SiIconModule } from '@siemens/element-ng/icon';
import { SiLoadingSpinnerModule } from '@siemens/element-ng/loading-spinner';
import { SiMenuModule } from '@siemens/element-ng/menu';
import { SiModalService } from '@siemens/element-ng/modal';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import { Subscription } from 'rxjs';
import { STANDARD_MODAL_CONFIG } from 'src/app/main/shared/const';
import { SaveData, Track, Workspace } from 'src/app/main/shared/DataTypes';
import { Waypoint } from 'src/app/main/shared/brouter-map/models/brouter-map.model';
import { BracketPipe } from 'src/app/main/shared/pipes/brackets.pipe';
import { StarPrefixPipe } from 'src/app/main/shared/pipes/star-prefix.pipe';
import { AccountService } from 'src/app/main/shared/services/account.service';
import { EventService } from 'src/app/main/shared/services/event.service';
import { ShareDataService } from 'src/app/main/shared/services/share-data.service';
import { WorkspaceService } from 'src/app/main/shared/services/workspace.service';

import { LoadModalComponent } from '../load-modal/load-modal.component';
import { SaveModalComponent } from '../save-modal/save-modal.component';
import { MapComponent } from './map/map.component';
import { TrackTableComponent } from './track-table/track-table.component';
import { TrackRenamePreview, TrackRenameResponse, TrackService } from './track.service';

@Component({
  selector: 'app-track',
  imports: [
    TranslateModule,
    SiEmptyStateModule,
    SiFormModule,
    FormsModule,
    SiIconModule,
    SiAccordionModule,
    SiCircleStatusModule,
    LoadModalComponent,
    SaveModalComponent,
    ReactiveFormsModule,
    TrackTableComponent,
    SiLoadingSpinnerModule,
    SiMenuModule,
    CdkMenuTrigger,
    StarPrefixPipe,
    MapComponent,
    BracketPipe
  ],
  templateUrl: './track.component.html',
  styleUrl: './track.component.scss'
})
export class TrackComponent implements AfterViewInit, OnDestroy {
  trackService = inject(TrackService);
  modalService = inject(SiModalService);
  eventService = inject(EventService);
  workspaceService = inject(WorkspaceService);
  toastNotificationService = inject(SiToastNotificationService);
  accountService = inject(AccountService);
  translateService = inject(TranslateService);
  siModal = inject(SiActionDialogService);
  shareDataService = inject(ShareDataService);

  ngAfterViewInit(): void {
    // Subscribe to Event to receive Track from Map
    this.eventService.moveTrackFromMapToTracksEvent$.subscribe((trackEndpoints: any) => {
      this.selectedTrack.set(undefined);
      this.trackFromMapLoading.set(true);
      this.trackService.getTrackFromIonlats(trackEndpoints).subscribe({
        next: (track: Track) => {
          this.selectedTrack.set(track);
          this.eventService.newTrackOpened();
        },
        error: (e: Error) => {
          this.toastNotificationService.showToastNotification({
            state: 'danger',
            title: this.translateService.instant('SHARED.ERROR'),
            message:
              this.translateService.instant('SHARED.ERROR_LOAD', {
                type: this.translateService.instant('TRACK.TRACK')
              }) +
              ': ' +
              e.message
          });
          this.trackFromMapLoading.set(false);
        },
        complete: () => {
          this.toastNotificationService.showToastNotification({
            state: 'success',
            title: this.translateService.instant('SHARED.SUCCESS'),
            message: this.translateService.instant('SHARED.SUCCESS_LOAD', {
              type: this.translateService.instant('TRACK.TRACK')
            })
          });
          this.trackFromMapLoading.set(false);
        }
      });
    });

    // Subscribe to Event to open Track from Schedule
    this.eventService.openTrackFromScheduleEvent$.subscribe(
      ({ trackId, trackParameters, trackName }) => {
        const dialog: ActionDialog = {
          type: 'confirmation',
          heading: this.translateService.instant('MAP.OVERRIDE_TRACK_HEADING'),
          message: this.translateService.instant('MAP.OVERRIDE_TRACK_MESSAGE')
        };

        if (this.selectedTrack()) {
          this.siModal.showActionDialog(dialog).subscribe(result => {
            if (result === 'confirm') {
              this.findAndOpenTrack(trackId, trackParameters, trackName);
            }
          });
        } else {
          this.findAndOpenTrack(trackId, trackParameters, trackName);
        }
      }
    );

    this.tourDataSubscription.add(
      this.eventService.loadTourTrackEvent$.subscribe(trackData => {
        if (!this.hasTourTrackSnapshot) {
          this.tourTrackSnapshot = this.selectedTrack();
          this.hasTourTrackSnapshot = true;
        }
        this.selectedTrack.set(this.trackService.getTrackFromJSON(trackData));
        this.eventService.newTrackOpened();
      })
    );

    this.tourDataSubscription.add(
      this.eventService.clearTourDataEvent$.subscribe(() => {
        if (!this.hasTourTrackSnapshot) {
          return;
        }

        this.selectedTrack.set(this.tourTrackSnapshot);
        this.stopImportTrackFromMap();
        this.tourTrackSnapshot = undefined;
        this.hasTourTrackSnapshot = false;
      })
    );
  }

  readonly trackFromMapLoading = signal<boolean>(false);
  private trackSubscription: Subscription | null = null;
  private readonly tourDataSubscription = new Subscription();
  private tourTrackSnapshot: Track | undefined;
  private hasTourTrackSnapshot = false;
  readonly buttonsDisabled = signal<boolean>(false); // Gets updated via event from TrackTableComponent

  readonly availableWorkspaces = this.shareDataService.availableWorkspaces;
  readonly selectedTrack = signal<Track | undefined>(undefined);

  // Rename state
  readonly renameInProgress = signal(false);
  readonly canEdit = computed(() => !!this.selectedTrack()?.id);

  openRenameModal(template: TemplateRef<any>) {
    this.modalService.show(template, {
      ...STANDARD_MODAL_CONFIG,
      class: 'responsive-modal modal-dialog-centered'
    });
  }

  handleRenameTrack(data: { name: string; version: string; description: string }) {
    const track = this.selectedTrack();
    if (!track?.id) {
      return;
    }

    const trimmedName = data.name.trim();
    const trimmedVersion = data.version.trim();
    const trimmedDescription = data.description.trim();

    if (!trimmedName || !trimmedVersion) {
      this.toastNotificationService.showToastNotification({
        state: 'danger',
        title: this.translateService.instant('SHARED.ERROR'),
        message: this.translateService.instant('TRACK.RENAME.EMPTY_FIELDS')
      });
      return;
    }

    const currentVersion = track.version ?? '';
    const currentDescription = track.description?.trim() ?? '';
    const nameChanged = trimmedName !== track.name;
    const versionChanged = trimmedVersion !== currentVersion;
    const descriptionChanged = trimmedDescription !== currentDescription;

    if (!nameChanged && !versionChanged && !descriptionChanged) {
      return;
    }

    if (!nameChanged) {
      this.executeTrackRename(track.id, trimmedName, trimmedVersion, trimmedDescription);
      return;
    }

    this.trackService.previewRenameTrack(track.name).subscribe({
      next: (preview: TrackRenamePreview) => {
        const dialog: ActionDialog = {
          type: 'confirmation',
          heading: this.translateService.instant('TRACK.RENAME.CONFIRM_HEADING'),
          message: this.translateService.instant('TRACK.RENAME.CONFIRM_MESSAGE', {
            variantCount: preview.variantCount,
            scheduleCount: preview.affectedScheduleCount
          })
        };

        this.siModal.showActionDialog(dialog).subscribe(result => {
          if (result === 'confirm') {
            this.executeTrackRename(track.id!, trimmedName, trimmedVersion, trimmedDescription);
          }
        });
      },
      error: error => {
        this.showRenameError(error);
      }
    });
  }

  private executeTrackRename(
    trackId: number,
    newName: string,
    newVersion: string,
    newDescription: string
  ) {
    this.renameInProgress.set(true);

    this.trackService.renameTrack(trackId, newName, newVersion, newDescription).subscribe({
      next: (response: TrackRenameResponse) => {
        this.selectedTrack.update(track => {
          if (!track) {
            return track;
          }

          return {
            ...track,
            name: response.newName,
            version: response.newVersion,
            description: response.newDescription
          };
        });

        this.refreshAvailableWorkspaces();

        this.toastNotificationService.showToastNotification({
          state: 'success',
          title: this.translateService.instant('SHARED.SUCCESS'),
          message: this.translateService.instant('TRACK.RENAME.SUCCESS', {
            name: response.newName,
            version: response.newVersion
          })
        });
      },
      error: error => {
        this.showRenameError(error);
      },
      complete: () => {
        this.renameInProgress.set(false);
      }
    });
  }

  private refreshAvailableWorkspaces() {
    this.workspaceService.getAllWorkspaces().subscribe({
      next: (workspaces: Workspace[]) => {
        this.shareDataService.setAvailableWorkspaces(workspaces);
      }
    });
  }

  private showRenameError(error: any) {
    this.renameInProgress.set(false);
    const message =
      error?.error?.detail ??
      error?.message ??
      error?.statusText ??
      this.translateService.instant('TRACK.RENAME.FALLBACK_ERROR');

    this.toastNotificationService.showToastNotification({
      state: 'danger',
      title: this.translateService.instant('SHARED.ERROR'),
      message
    });
  }

  private findAndOpenTrack(
    trackId?: number,
    trackParameters?: { [key: string]: any },
    trackName?: string
  ): void {
    if (!trackId) {
      // Track data comes directly from Schedule, not from DB. We can directly set it without fetching.
      this.selectedTrack.set({
        name: trackName!,
        deleted: false,
        source: 'From Scratch',
        parameters: trackParameters!,
        tags: []
      });
      return;
    } else {
      // Find track in available workspaces
      const workspaces = this.availableWorkspaces();

      const trackToOpen = workspaces
        .flatMap(workspace => workspace.tracks)
        .flatMap(trackGroup => trackGroup.variants)
        .find(track => track.id === trackId);

      if (trackToOpen) {
        this.openTrackClicked(trackToOpen);
      } else {
        this.toastNotificationService.showToastNotification({
          state: 'danger',
          title: this.translateService.instant('SHARED.ERROR'),
          message: this.translateService.instant('TRACK.NOT_ACCESSIBLE')
        });
      }
    }
  }

  openTrackClicked(track: any) {
    const newTrack = { ...track };
    const trackId = track.id;
    // Check if track data already got initialized
    if (!track.parameters && trackId) {
      this.trackService.getTrackDataFromDB(trackId).subscribe((parameters: any) => {
        newTrack.parameters = parameters;
        this.selectedTrack.set(newTrack);
        console.log(newTrack);
        this.eventService.newTrackOpened();
      });
    } else {
      this.selectedTrack.set(newTrack);
      this.eventService.newTrackOpened();
    }
  }

  stopImportTrackFromMap() {
    this.trackFromMapLoading.set(false);
    this.trackSubscription?.unsubscribe();
    this.trackSubscription = null;
  }

  openModal(template: TemplateRef<any>): void {
    this.modalService.show(template, {
      ...STANDARD_MODAL_CONFIG,
      class: 'medium-modal'
    });
  }

  openSaveTrackModal(template: TemplateRef<any>, inputValues: Record<string, any> = {}) {
    this.modalService.show(template, {
      ...STANDARD_MODAL_CONFIG,
      inputValues,
      class: 'responsive-modal modal-dialog-centered'
    });
  }

  closeModal(modalRef: any): void {
    modalRef.hide();
  }

  handleSaveNewTrack(data: SaveData) {
    if (this.selectedTrack()) {
      const newTrack: Track = {
        ...this.selectedTrack()!,
        name: data.name,
        version: data.version,
        description: data.description,
        deleted: false
      };

      this.trackService.saveNewTrackToDB(newTrack, data.workspaceId).subscribe({
        error: e => {
          this.toastNotificationService.showToastNotification({
            state: 'danger',
            title: this.translateService.instant('SHARED.ERROR'),
            message:
              this.translateService.instant('SHARED.ERROR_SAVE', {
                type: this.translateService.instant('TRACK.TRACK')
              }) +
              ': ' +
              e.statusText
          });
        },
        next: (response: any) => {
          // Update available workspaces to receive the new track
          this.workspaceService.getAllWorkspaces().subscribe({
            next: (workspaces: Workspace[]) => {
              this.shareDataService.setAvailableWorkspaces(workspaces);
            }
          });
          newTrack.id = response.id;
          this.selectedTrack.set(newTrack);
        },
        complete: () => {
          this.toastNotificationService.showToastNotification({
            state: 'success',
            title: this.translateService.instant('SHARED.SUCCESS'),
            message: this.translateService.instant('SHARED.SUCCESS_SAVE', {
              type: this.translateService.instant('TRACK.TRACK'),
              name: data.name
            })
          });
        }
      });
    }
  }

  ngOnDestroy(): void {
    // Unsubscribe from the subscription to avoid memory leaks
    if (this.trackSubscription) {
      this.trackSubscription.unsubscribe();
    }
    this.tourDataSubscription.unsubscribe();
  }
  readonly jsonFileInput = viewChild<ElementRef>('jsonFileInput');
  readonly driveFileInput = viewChild<ElementRef>('driveFileInput');

  openFileSelector(type: 'json' | 'drive') {
    if (type === 'json') {
      this.jsonFileInput()!.nativeElement.click();
    } else if (type === 'drive') {
      this.driveFileInput()!.nativeElement.click();
    }
  }

  onJSONFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      console.log(file);
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const jsonData = JSON.parse(reader.result as string);
          this.selectedTrack.set(this.trackService.getTrackFromJSON(jsonData));
          this.eventService.newTrackOpened();
          this.displayFileImportSuccesToast();
        } catch (error) {
          const type = this.translateService.instant('TRACK.TRACK');
          const message =
            error instanceof Error
              ? error.message
              : this.translateService.instant('SHARED.ERROR_LOAD', {
                  type: type
                });

          this.toastNotificationService.showToastNotification({
            state: 'danger',
            title: this.translateService.instant('SHARED.ERROR_LOAD', {
              type: type
            }),
            message
          });
        }
      };
      reader.readAsText(file);
    }
  }

  onDriveFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      try {
        this.trackService.getTrackFromDriveFile(file).subscribe({
          next: (track: Track) => {
            this.selectedTrack.set(track);
            this.eventService.newTrackOpened();
            this.displayFileImportSuccesToast();
          },
          error: (e: Error) => {
            this.toastNotificationService.showToastNotification({
              state: 'danger',
              title: this.translateService.instant('SHARED.ERROR_LOAD', {
                type: this.translateService.instant('TRACK.TRACK')
              }),
              message: e.message
            });
          }
        });
      } catch (error) {
        console.error('Invalid DRIVE file:', error);
      }
    }
  }

  displayFileImportSuccesToast() {
    this.toastNotificationService.showToastNotification({
      state: 'success',
      title: this.translateService.instant('SHARED.SUCCESS'),
      message: this.translateService.instant('SHARED.SUCCESS_LOAD', {
        type: this.translateService.instant('TRACK.TRACK')
      })
    });
  }

  exportTrackDataAsJSON() {
    const jsonString = JSON.stringify(this.selectedTrack()?.parameters);
    const trackNameOfCreatedTrack = this.selectedTrack()?.name;

    // Create a blob from the JSON string
    const blob = new Blob([jsonString], { type: 'application/json' });

    // Create a temporary URL for the blob
    const url = URL.createObjectURL(blob);

    // Create a link element
    const link = document.createElement('a');
    link.href = url;

    // Set the filename for download
    link.download =
      trackNameOfCreatedTrack + '_user_GID_' + this.accountService.accountSummary()?.gid + '.json';

    this.simulateLinkClick(link);
    URL.revokeObjectURL(url);
  }

  exportTrackDataAsXNDS() {
    this.trackService.getXNDSFileFromTrack(this.selectedTrack()!).subscribe({
      next: (response: any) => {
        const blob = new Blob([response], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.selectedTrack()?.name + '.xnds';
        this.simulateLinkClick(link);
        URL.revokeObjectURL(url);
      },
      error: (e: Error) => {
        this.toastNotificationService.showToastNotification({
          state: 'danger',
          title: this.translateService.instant('SHARED.ERROR_EXPORT', {
            type: this.translateService.instant('TRACK.TRACK')
          }),
          message: e.message
        });
      }
    });
  }

  simulateLinkClick(link: HTMLAnchorElement) {
    // Append the link to the document body
    document.body.appendChild(link);
    // Trigger the click event on the link to initiate download
    link.click();
    // Clean up: remove the link and revoke the URL
    document.body.removeChild(link);
  }

  readonly isMapOpened = signal<boolean>(false);
  readonly mapInitialWaypoints = signal<Waypoint[]>([]);

  toggleMap() {
    this.isMapOpened.update(value => !value);
  }

  openMapWithRoute(waypoints: Waypoint[]) {
    this.mapInitialWaypoints.set([...waypoints]);
    this.isMapOpened.set(true);
  }

  createNewTrack() {
    this.selectedTrack.set({
      name: 'New Track',
      deleted: false,
      source: 'From Scratch',
      parameters: this.trackService.getEmptyTrackParameters(),
      tags: []
    });
    this.eventService.newTrackOpened();
  }

  closeTrackClicked() {
    this.selectedTrack.set(undefined);
  }
}
